import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LayoutDashboard, Receipt, FileText, Tags, Menu, X, Settings, Download, Upload, Calculator, LogOut, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { v4 as uuidv4 } from 'uuid';

// Import pages
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import Bills from "@/pages/Bills";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import IncomeDetails from "@/pages/IncomeDetails";
import ExpenseDetails from "@/pages/ExpenseDetails";
import SummaryAnalytics from "@/pages/SummaryAnalytics";
import CycleSettings from "@/pages/CycleSettings";
import BudgetPlanner from "@/pages/BudgetPlanner";

// ============== AUTH CONSTANTS ==============
const AUTH_TOKEN_KEY = 'bkfinance_session_auth';
const APP_PASSWORD_KEY = 'bkfinance_app_password';
const DEFAULT_PASSWORD = 'KhatriFinance@2002';
const MASTER_PASSWORD = 'Anita@Birendra';

// Get current app password (from localStorage or default)
const getAppPassword = () => {
  return localStorage.getItem(APP_PASSWORD_KEY) || DEFAULT_PASSWORD;
};

// ============== LOGIN COMPONENT ==============
const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    setTimeout(() => {
      const currentPassword = getAppPassword();
      if (password === currentPassword) {
        // Use sessionStorage so login is required every time browser closes
        sessionStorage.setItem(AUTH_TOKEN_KEY, "authenticated");
        onLogin();
      } else {
        setError("Incorrect password");
        setPassword("");
      }
      setLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white rounded-3xl shadow-lg border border-stone-200">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 rounded-2xl mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-stone-900">Khatri Finance</h1>
          <p className="text-stone-500 mt-2">Enter password to continue</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 text-center text-lg rounded-xl border-stone-200 focus:border-stone-900 focus:ring-stone-900"
              autoFocus
              required
            />
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl">
              <p className="text-rose-700 text-sm text-center font-medium">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-12 rounded-xl bg-stone-900 hover:bg-stone-800 text-lg font-medium"
            disabled={loading}
          >
            {loading ? "Verifying..." : "Unlock"}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-stone-400 mt-8">
          Your personal finance tracker
        </p>
      </Card>
    </div>
  );
};

// ============== UTILITY FUNCTIONS ==============

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Calculate cycle key from date based on cycle config
export const getCycleKey = (dateStr, cycleConfig) => {
  const date = new Date(dateStr);
  
  // If custom dates are set, check if date falls within the range
  if (cycleConfig.customStart && cycleConfig.customEnd) {
    const customStart = new Date(cycleConfig.customStart);
    const customEnd = new Date(cycleConfig.customEnd);
    
    // If date is within custom range, assign to this cycle
    if (date >= customStart && date <= customEnd) {
      return cycleConfig.customStart;
    }
    // If date is before custom start, assign to "before" cycle
    if (date < customStart) {
      return "before-cycle";
    }
    // If date is after custom end, assign to "after" cycle
    return "after-cycle";
  }
  
  // Fallback to default logic
  const { startDay } = cycleConfig;
  
  let year = date.getFullYear();
  let month = date.getMonth();
  const day = date.getDate();
  
  // If current day is before startDay, the cycle started in previous month
  if (day < startDay) {
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }
  
  // Cycle key is the start date of the cycle
  const cycleStart = new Date(year, month, startDay);
  return cycleStart.toISOString().split('T')[0];
};

// Get cycle date range from cycle key
export const getCycleDateRange = (cycleKey, cycleConfig) => {
  // If custom dates are set, use them
  if (cycleConfig.customStart && cycleConfig.customEnd) {
    const startDate = new Date(cycleConfig.customStart);
    const endDate = new Date(cycleConfig.customEnd);
    
    return {
      start: startDate,
      end: endDate,
      label: `${formatDateShort(startDate)} → ${formatDateShort(endDate)}`
    };
  }
  
  // Fallback to default cycle logic
  const { startDay, endDay } = cycleConfig;
  const startDate = new Date(cycleKey);
  
  let endYear = startDate.getFullYear();
  let endMonthNum = startDate.getMonth() + 1;
  
  if (endMonthNum > 11) {
    endMonthNum = 0;
    endYear += 1;
  }
  
  const endDate = new Date(endYear, endMonthNum, endDay);
  
  return {
    start: startDate,
    end: endDate,
    label: `${formatDateShort(startDate)} → ${formatDateShort(endDate)}`
  };
};

// Format date short
export const formatDateShort = (date) => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
};

// Format date with year
export const formatDateFull = (date) => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Get all unique cycle keys from transactions and bills
export const getAllCycleKeys = (transactions, bills, cycleConfig) => {
  const keys = new Set();
  
  // If custom cycle is set, only show that cycle
  if (cycleConfig.customStart) {
    keys.add(cycleConfig.customStart);
    return Array.from(keys);
  }
  
  transactions.forEach(t => {
    if (t.cycleKey && t.cycleKey !== "before-cycle" && t.cycleKey !== "after-cycle") {
      keys.add(t.cycleKey);
    }
  });
  
  bills.forEach(b => {
    if (b.cycleKey && b.cycleKey !== "before-cycle" && b.cycleKey !== "after-cycle") {
      keys.add(b.cycleKey);
    }
  });
  
  // Add current cycle if empty
  if (keys.size === 0) {
    const today = new Date().toISOString().split('T')[0];
    keys.add(getCycleKey(today, cycleConfig));
  }
  
  return Array.from(keys).sort().reverse();
};

// Calculate summary for a cycle
export const calculateCycleSummary = (cycleKey, transactions, bills, cycleConfig = {}) => {
  let cycleTransactions;
  let cycleBills;
  
  // If custom cycle, filter by date range
  if (cycleConfig?.customStart && cycleConfig?.customEnd) {
    const startDate = new Date(cycleConfig.customStart);
    const endDate = new Date(cycleConfig.customEnd);
    
    cycleTransactions = transactions.filter(t => {
      const txDate = new Date(t.dateISO);
      return txDate >= startDate && txDate <= endDate;
    });
    
    cycleBills = bills.filter(b => b.cycleKey === cycleKey);
  } else {
    cycleTransactions = transactions.filter(t => t.cycleKey === cycleKey);
    cycleBills = bills.filter(b => b.cycleKey === cycleKey);
  }
  
  const totalIncome = cycleTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
    
  const totalExpenses = cycleTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
    
  const netBalance = totalIncome - totalExpenses;
  
  const plannedBillsTotal = cycleBills.reduce((sum, b) => sum + b.amount, 0);
  const paidBillsTotal = cycleBills.filter(b => b.isPaid).reduce((sum, b) => sum + b.amount, 0);
  const leftToPay = plannedBillsTotal - paidBillsTotal;
  const balanceAfterBills = totalIncome - plannedBillsTotal;
  
  return {
    cycleKey,
    totalIncome,
    totalExpenses,
    netBalance,
    plannedBillsTotal,
    paidBillsTotal,
    leftToPay,
    balanceAfterBills
  };
};

// ============== LOCAL STORAGE ==============

const STORAGE_KEYS = {
  CATEGORIES: 'bkfinance_categories',
  TRANSACTIONS: 'bkfinance_transactions',
  BILLS: 'bkfinance_bills',
  CYCLE_CONFIG: 'bkfinance_cycle_config',
  SELECTED_CYCLE: 'bkfinance_selected_cycle'
};

const DEFAULT_CYCLE_CONFIG = { 
  startDay: 1, 
  endDay: 28,
  customStart: null,
  customEnd: null
};

const loadFromStorage = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Failed to save to localStorage:', e);
  }
};

// Generate sample data
const generateSampleData = (cycleConfig) => {
  const today = new Date();
  
  // Generate 3 cycle keys
  const cycles = [];
  for (let i = 0; i < 3; i++) {
    const date = new Date(today);
    date.setMonth(date.getMonth() - i);
    cycles.push(getCycleKey(date.toISOString().split('T')[0], cycleConfig));
  }
  
  const categories = [
    { id: uuidv4(), name: 'Salary', type: 'income' },
    { id: uuidv4(), name: 'Freelance', type: 'income' },
    { id: uuidv4(), name: 'Investments', type: 'income' },
    { id: uuidv4(), name: 'Rent', type: 'expense' },
    { id: uuidv4(), name: 'Utilities', type: 'expense' },
    { id: uuidv4(), name: 'Food', type: 'expense' },
    { id: uuidv4(), name: 'Transport', type: 'expense' },
    { id: uuidv4(), name: 'Entertainment', type: 'expense' },
    { id: uuidv4(), name: 'Shopping', type: 'expense' },
  ];
  
  const catMap = {};
  categories.forEach(c => catMap[c.name] = c.id);
  
  const transactions = [];
  const bills = [];
  
  cycles.forEach((cycleKey, idx) => {
    const cycleStart = new Date(cycleKey);
    
    // Income transactions
    transactions.push({
      id: uuidv4(),
      dateISO: new Date(cycleStart.getTime() + 86400000).toISOString().split('T')[0],
      cycleKey,
      type: 'income',
      categoryId: catMap['Salary'],
      amount: 4500 + (idx * 100),
      notes: 'Monthly salary',
      createdAt: new Date().toISOString()
    });
    
    if (idx < 2) {
      transactions.push({
        id: uuidv4(),
        dateISO: new Date(cycleStart.getTime() + 86400000 * 5).toISOString().split('T')[0],
        cycleKey,
        type: 'income',
        categoryId: catMap['Freelance'],
        amount: 600 + (idx * 200),
        notes: 'Project work',
        createdAt: new Date().toISOString()
      });
    }
    
    // Expense transactions
    transactions.push({
      id: uuidv4(),
      dateISO: new Date(cycleStart.getTime() + 86400000 * 3).toISOString().split('T')[0],
      cycleKey,
      type: 'expense',
      categoryId: catMap['Rent'],
      amount: 1200,
      notes: 'Monthly rent',
      createdAt: new Date().toISOString()
    });
    
    transactions.push({
      id: uuidv4(),
      dateISO: new Date(cycleStart.getTime() + 86400000 * 7).toISOString().split('T')[0],
      cycleKey,
      type: 'expense',
      categoryId: catMap['Food'],
      amount: 450 + (idx * 50),
      notes: 'Groceries',
      createdAt: new Date().toISOString()
    });
    
    transactions.push({
      id: uuidv4(),
      dateISO: new Date(cycleStart.getTime() + 86400000 * 10).toISOString().split('T')[0],
      cycleKey,
      type: 'expense',
      categoryId: catMap['Utilities'],
      amount: 180 + (idx * 20),
      notes: 'Electric & Gas',
      createdAt: new Date().toISOString()
    });
    
    transactions.push({
      id: uuidv4(),
      dateISO: new Date(cycleStart.getTime() + 86400000 * 15).toISOString().split('T')[0],
      cycleKey,
      type: 'expense',
      categoryId: catMap['Transport'],
      amount: 120,
      notes: 'Monthly travel',
      createdAt: new Date().toISOString()
    });
    
    transactions.push({
      id: uuidv4(),
      dateISO: new Date(cycleStart.getTime() + 86400000 * 20).toISOString().split('T')[0],
      cycleKey,
      type: 'expense',
      categoryId: catMap['Entertainment'],
      amount: 150 + (idx * 30),
      notes: 'Movies & dining',
      createdAt: new Date().toISOString()
    });
    
    // Bills
    bills.push({
      id: uuidv4(),
      cycleKey,
      name: 'Rent',
      categoryId: catMap['Rent'],
      amount: 1200,
      isPaid: true,
      dueDateISO: new Date(cycleStart.getTime() + 86400000 * 5).toISOString().split('T')[0]
    });
    
    bills.push({
      id: uuidv4(),
      cycleKey,
      name: 'Utilities',
      categoryId: catMap['Utilities'],
      amount: 180 + (idx * 20),
      isPaid: idx > 0,
      dueDateISO: new Date(cycleStart.getTime() + 86400000 * 15).toISOString().split('T')[0]
    });
    
    bills.push({
      id: uuidv4(),
      cycleKey,
      name: 'Internet',
      amount: 45,
      isPaid: true,
      dueDateISO: new Date(cycleStart.getTime() + 86400000 * 10).toISOString().split('T')[0]
    });
    
    bills.push({
      id: uuidv4(),
      cycleKey,
      name: 'Phone',
      amount: 35,
      isPaid: idx > 1,
      dueDateISO: new Date(cycleStart.getTime() + 86400000 * 20).toISOString().split('T')[0]
    });
  });
  
  return { categories, transactions, bills };
};

// ============== APP CONTEXT ==============

export const AppContext = createContext();
export const useApp = () => useContext(AppContext);

// ============== HEADER COMPONENT ==============

const Header = ({ selectedCycle, setSelectedCycle, cycleKeys, cycleConfig, onExport, onImport, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/transactions", icon: Receipt, label: "Transactions" },
    { to: "/bills", icon: FileText, label: "Bills" },
    { to: "/categories", icon: Tags, label: "Categories" },
    { to: "/planner", icon: Calculator, label: "Planner" },
  ];

  const currentCycleRange = selectedCycle ? getCycleDateRange(selectedCycle, cycleConfig) : null;

  // Navigate to previous/next cycle
  const goToPrevCycle = () => {
    const idx = cycleKeys.indexOf(selectedCycle);
    if (idx < cycleKeys.length - 1) {
      setSelectedCycle(cycleKeys[idx + 1]);
    }
  };

  const goToNextCycle = () => {
    const idx = cycleKeys.indexOf(selectedCycle);
    if (idx > 0) {
      setSelectedCycle(cycleKeys[idx - 1]);
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-stone-200/50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <h1 
            className="font-heading text-2xl text-stone-900 tracking-tight cursor-pointer" 
            data-testid="app-title"
            onClick={() => navigate('/')}
          >
            Khatri Finance
          </h1>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2" data-testid="desktop-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "bg-stone-900 text-white"
                      : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Cycle Selector & Actions */}
          <div className="flex items-center gap-2">
            {/* Prev/Next buttons */}
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevCycle}
              disabled={cycleKeys.indexOf(selectedCycle) >= cycleKeys.length - 1}
              className="hidden sm:flex h-8 w-8"
              data-testid="prev-cycle-btn"
            >
              ‹
            </Button>

            <Select value={selectedCycle} onValueChange={setSelectedCycle}>
              <SelectTrigger className="w-[160px] md:w-[200px] rounded-full border-stone-200 text-xs md:text-sm" data-testid="cycle-selector">
                <SelectValue placeholder="Select cycle">
                  {currentCycleRange?.label || 'Select cycle'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {cycleKeys.map((key) => {
                  const range = getCycleDateRange(key, cycleConfig);
                  return (
                    <SelectItem key={key} value={key} data-testid={`cycle-option-${key}`}>
                      {range.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextCycle}
              disabled={cycleKeys.indexOf(selectedCycle) <= 0}
              className="hidden sm:flex h-8 w-8"
              data-testid="next-cycle-btn"
            >
              ›
            </Button>

            {/* Settings button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              className="h-8 w-8"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>

            {/* Logout button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-stone-200/50" data-testid="mobile-nav">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? "bg-stone-900 text-white"
                        : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
              <div className="flex gap-2 px-4 pt-2 border-t border-stone-100 mt-2">
                <Button variant="outline" size="sm" onClick={onExport} className="flex-1">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
                <Button variant="outline" size="sm" onClick={onImport} className="flex-1">
                  <Upload className="w-4 h-4 mr-2" /> Import
                </Button>
              </div>
              <div className="px-4 pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onLogout} 
                  className="w-full text-rose-600 border-rose-200 hover:bg-rose-50"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// ============== MAIN APP ==============

function AppContent() {
  const [cycleConfig, setCycleConfig] = useState(() => 
    loadFromStorage(STORAGE_KEYS.CYCLE_CONFIG, DEFAULT_CYCLE_CONFIG)
  );
  const [categories, setCategories] = useState(() => 
    loadFromStorage(STORAGE_KEYS.CATEGORIES, [])
  );
  const [transactions, setTransactions] = useState(() => 
    loadFromStorage(STORAGE_KEYS.TRANSACTIONS, [])
  );
  const [bills, setBills] = useState(() => 
    loadFromStorage(STORAGE_KEYS.BILLS, [])
  );
  const [selectedCycle, setSelectedCycle] = useState(() => 
    loadFromStorage(STORAGE_KEYS.SELECTED_CYCLE, null)
  );
  const [loading, setLoading] = useState(false);

  // Calculate cycle keys
  const cycleKeys = getAllCycleKeys(transactions, bills, cycleConfig);

  // Set initial selected cycle
  useEffect(() => {
    if (!selectedCycle && cycleKeys.length > 0) {
      setSelectedCycle(cycleKeys[0]);
    }
  }, [cycleKeys, selectedCycle]);

  // Save to localStorage when data changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.CYCLE_CONFIG, cycleConfig);
  }, [cycleConfig]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.CATEGORIES, categories);
  }, [categories]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.TRANSACTIONS, transactions);
  }, [transactions]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.BILLS, bills);
  }, [bills]);

  useEffect(() => {
    if (selectedCycle) {
      saveToStorage(STORAGE_KEYS.SELECTED_CYCLE, selectedCycle);
    }
  }, [selectedCycle]);

  // Load sample data if empty
  const loadSampleData = useCallback(() => {
    const { categories: sampleCats, transactions: sampleTx, bills: sampleBills } = generateSampleData(cycleConfig);
    setCategories(sampleCats);
    setTransactions(sampleTx);
    setBills(sampleBills);
    toast.success('Sample data loaded!');
  }, [cycleConfig]);

  // Update cycle config and recompute all cycleKeys
  const updateCycleConfig = useCallback((newConfig) => {
    setCycleConfig(newConfig);
    
    // Recompute cycleKey for all transactions
    setTransactions(prev => prev.map(t => ({
      ...t,
      cycleKey: getCycleKey(t.dateISO, newConfig)
    })));
    
    toast.success('Budget cycle updated!');
  }, []);

  // CRUD operations for categories
  const addCategory = useCallback((category) => {
    const newCategory = { ...category, id: uuidv4() };
    setCategories(prev => [...prev, newCategory]);
    return newCategory;
  }, []);

  const updateCategory = useCallback((id, updates) => {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  }, []);

  const deleteCategory = useCallback((id) => {
    setCategories(prev => prev.filter(c => c.id !== id));
  }, []);

  // CRUD operations for transactions
  const addTransaction = useCallback((transaction) => {
    const cycleKey = getCycleKey(transaction.dateISO, cycleConfig);
    const newTransaction = {
      ...transaction,
      id: uuidv4(),
      cycleKey,
      createdAt: new Date().toISOString()
    };
    setTransactions(prev => [...prev, newTransaction]);
    return newTransaction;
  }, [cycleConfig]);

  const updateTransaction = useCallback((id, updates) => {
    setTransactions(prev => prev.map(t => {
      if (t.id === id) {
        const updated = { ...t, ...updates };
        if (updates.dateISO) {
          updated.cycleKey = getCycleKey(updates.dateISO, cycleConfig);
        }
        return updated;
      }
      return t;
    }));
  }, [cycleConfig]);

  const deleteTransaction = useCallback((id) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
  }, []);

  // CRUD operations for bills
  const addBill = useCallback((bill) => {
    const newBill = { ...bill, id: uuidv4() };
    setBills(prev => [...prev, newBill]);
    return newBill;
  }, []);

  const updateBill = useCallback((id, updates) => {
    setBills(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  }, []);

  const deleteBill = useCallback((id) => {
    // First get the bill to check for linked transaction
    const bill = bills.find(b => b.id === id);
    if (bill?.linkedTransactionId) {
      setTransactions(prev => prev.filter(t => t.id !== bill.linkedTransactionId));
    }
    setBills(prev => prev.filter(b => b.id !== id));
  }, [bills]);

  const toggleBillPaid = useCallback((id) => {
    setBills(prevBills => {
      const bill = prevBills.find(b => b.id === id);
      if (!bill) return prevBills;
      
      const newIsPaid = !bill.isPaid;
      const isIncome = bill.type === 'income';
      
      if (newIsPaid) {
        // Bill/Income is being marked as PAID/RECEIVED
        // Check if already has a linked transaction - prevent duplicates
        if (bill.linkedTransactionId) {
          // Already has a linked transaction, just update status
          toast.info(isIncome ? 'Already marked as received' : 'Already marked as paid');
          return prevBills.map(b => b.id === id ? { ...b, isPaid: true } : b);
        }
        
        // Always use current date as the paid/received date
        const today = new Date().toISOString().split('T')[0];
        const newTxId = uuidv4();
        
        // Create the transaction with today's date
        const newTransaction = {
          id: newTxId,
          dateISO: today,
          cycleKey: bill.cycleKey,
          type: isIncome ? 'income' : 'expense',
          categoryId: bill.categoryId || null,
          amount: bill.amount,
          notes: isIncome ? `Income received: ${bill.name}` : `Bill payment: ${bill.name}`,
          createdAt: new Date().toISOString(),
          linkedBillId: bill.id
        };
        
        // Add transaction
        setTransactions(prevTx => {
          // Double check no duplicate exists
          const exists = prevTx.some(t => t.linkedBillId === bill.id);
          if (exists) {
            return prevTx;
          }
          return [...prevTx, newTransaction];
        });
        
        toast.success(isIncome 
          ? `Income marked as received - added to transactions`
          : `Bill marked as paid - expense added to transactions`
        );
        
        // Update the bill with linked transaction ID
        return prevBills.map(b => 
          b.id === id 
            ? { ...b, isPaid: true, linkedTransactionId: newTxId, paidDateISO: today }
            : b
        );
      } else {
        // Bill/Income is being marked as UNPAID/NOT RECEIVED - remove the linked transaction
        if (bill.linkedTransactionId) {
          setTransactions(prevTx => prevTx.filter(t => t.id !== bill.linkedTransactionId));
          toast.info(isIncome 
            ? `Income marked as not received - removed from transactions`
            : `Bill marked as unpaid - expense removed from transactions`
          );
        }
        
        return prevBills.map(b => 
          b.id === id 
            ? { ...b, isPaid: false, linkedTransactionId: null, paidDateISO: null }
            : b
        );
      }
    });
  }, []);

  // Export data - includes ALL app data
  const exportData = useCallback(() => {
    // Get Budget Planner items from localStorage (categories are already in main app)
    const budgetItems = JSON.parse(localStorage.getItem('bkfinance_budget_items') || '[]');
    
    const data = {
      version: 2,
      exportedAt: new Date().toISOString(),
      appName: 'Khatri Finance',
      // Main app data
      cycleConfig,
      categories,
      transactions,
      bills,
      // Budget Planner items (uses same categories as main app)
      budgetItems
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `khatri-finance-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('All data exported successfully!');
  }, [cycleConfig, categories, transactions, bills]);

  // Import data - clears existing data and imports fresh
  const importData = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target.result);
            
            // Clear ALL existing data first
            setCategories([]);
            setTransactions([]);
            setBills([]);
            setCycleConfig({ startDay: 1, endDay: 28, customStart: null, customEnd: null });
            localStorage.removeItem('bkfinance_budget_items');
            
            // Import new data
            if (data.categories) setCategories(data.categories);
            if (data.transactions) setTransactions(data.transactions);
            if (data.bills) setBills(data.bills);
            if (data.cycleConfig) setCycleConfig(data.cycleConfig);
            
            // Import Budget Planner items
            if (data.budgetItems) {
              localStorage.setItem('bkfinance_budget_items', JSON.stringify(data.budgetItems));
            }
            
            toast.success('Data imported successfully! All previous data cleared.');
          } catch (err) {
            toast.error('Invalid backup file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, []);

  // Get summary for current cycle
  const currentSummary = selectedCycle 
    ? calculateCycleSummary(selectedCycle, transactions, bills, cycleConfig)
    : null;

  // Get all summaries for chart
  const allSummaries = cycleKeys.map(key => calculateCycleSummary(key, transactions, bills, cycleConfig));

  // Change password function
  const changeAppPassword = useCallback((masterPass, newPass) => {
    if (masterPass !== MASTER_PASSWORD) {
      return { success: false, error: 'Incorrect master password' };
    }
    if (!newPass || newPass.length < 4) {
      return { success: false, error: 'New password must be at least 4 characters' };
    }
    localStorage.setItem(APP_PASSWORD_KEY, newPass);
    return { success: true };
  }, []);

  const contextValue = {
    cycleConfig,
    updateCycleConfig,
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    transactions,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    bills,
    addBill,
    updateBill,
    deleteBill,
    toggleBillPaid,
    selectedCycle,
    setSelectedCycle,
    cycleKeys,
    currentSummary,
    allSummaries,
    loading,
    loadSampleData,
    exportData,
    importData,
    changeAppPassword,
    getCategoryById: (id) => categories.find(c => c.id === id),
    getCycleDateRange: (key) => getCycleDateRange(key, cycleConfig),
  };

  // Logout function
  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    window.location.reload();
  }, []);

  return (
    <AppContext.Provider value={contextValue}>
      <div className="app-container bg-stone-50 min-h-screen">
        <Header
          selectedCycle={selectedCycle}
          setSelectedCycle={setSelectedCycle}
          cycleKeys={cycleKeys}
          cycleConfig={cycleConfig}
          onExport={exportData}
          onImport={importData}
          onLogout={handleLogout}
        />
        <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 pb-20">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/bills" element={<Bills />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/categories/:categoryId" element={<CategoryDetail />} />
            <Route path="/income" element={<IncomeDetails />} />
            <Route path="/expenses" element={<ExpenseDetails />} />
            <Route path="/analytics" element={<SummaryAnalytics />} />
            <Route path="/settings" element={<CycleSettings />} />
            <Route path="/planner" element={<BudgetPlanner />} />
          </Routes>
        </main>
        
        {/* Made by Benjamin Badge */}
        <a
          href="https://www.instagram.com/benjaminkhatri?igsh=MXczdzlvanRueTJkNg=="
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-4 right-4 flex items-center gap-2 px-3 py-2 bg-stone-900 text-white text-xs font-medium rounded-full shadow-lg hover:bg-stone-800 transition-all hover:scale-105 z-50"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          Made by Benjamin
        </a>
        
        <Toaster position="top-right" richColors closeButton />
      </div>
    </AppContext.Provider>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Use sessionStorage - requires login every time browser is closed/reopened
    return sessionStorage.getItem(AUTH_TOKEN_KEY) === "authenticated";
  });

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // If authenticated, show the app
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
